import { useEffect, useMemo, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { LedgerEntry, Member, Season } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { hasPermission } from "../../auth/permissions";
import { SortableHeader } from "../../components/SortableHeader";
import { formatNumber } from "../../lib/format";
import { fetchAllLedger } from "../../lib/ledger";

interface State {
  status: "loading" | "ready" | "error";
  seasons: Season[];
  selectedSeasonID: string;
  members: Member[];
  ledger: LedgerEntry[];
  ledgerLoading: boolean;
  errorMessage: string | null;
}

const initial: State = {
  status: "loading",
  seasons: [],
  selectedSeasonID: "",
  members: [],
  ledger: [],
  ledgerLoading: false,
  errorMessage: null
};

type SortKey = "email" | "role" | "available" | "received" | "given" | "coinsGiven";

interface SortState {
  key: SortKey;
  dir: "asc" | "desc";
}

interface PersonRow {
  id: string;
  email: string;
  name: string;
  role: string;
  availableCoins: number;
  gumballsReceived: number;
  gumballsGiven: number;
  coinsGiven: number;
}

const roleRank: Record<string, number> = {
  admin: 0,
  dynasty: 1,
  judicious: 2,
  execution: 3,
  ic: 4
};

const ASSIGNABLE_ROLES = ["admin", "dynasty", "judicious", "execution", "ic"] as const;

export function People() {
  const { me } = useAuth();
  const canChangeRoles = me !== null && hasPermission(me, "members:role:change");
  const canDeleteMembers = me !== null && hasPermission(me, "members:delete");
  const currentUserID = me?.user.id ?? null;
  const [state, setState] = useState<State>(initial);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortState>({ key: "email", dir: "asc" });
  const [savingId, setSavingId] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function changeSeason(seasonID: string) {
    setState((previous) => ({
      ...previous,
      selectedSeasonID: seasonID,
      ledger: [],
      ledgerLoading: true,
      errorMessage: null
    }));
    try {
      const ledger = await fetchAllLedger(seasonID);
      setState((previous) => ({
        ...previous,
        ledger,
        ledgerLoading: false
      }));
    } catch (caught) {
      setState((previous) => ({
        ...previous,
        ledgerLoading: false,
        errorMessage: messageFor(caught)
      }));
    }
  }

  async function handleRoleChange(userID: string, newRole: string) {
    setRoleError(null);
    setSavingId(userID);
    try {
      await apiClient.changeMemberRole(userID, newRole);
      setState((previous) => ({
        ...previous,
        members: previous.members.map((member) =>
          member.id === userID ? { ...member, role: newRole } : member
        )
      }));
    } catch (caught) {
      setRoleError(messageFor(caught));
    } finally {
      setSavingId(null);
    }
  }

  async function handleDelete(userID: string, email: string) {
    if (!window.confirm(`Remove ${email}? They'll lose access immediately. History is preserved.`)) {
      return;
    }
    setRoleError(null);
    setDeletingId(userID);
    try {
      await apiClient.deleteMember(userID);
      setState((previous) => ({
        ...previous,
        members: previous.members.filter((member) => member.id !== userID)
      }));
    } catch (caught) {
      setRoleError(messageFor(caught));
    } finally {
      setDeletingId(null);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setState({ ...initial });

    async function load() {
      try {
        const [membersResponse, seasonsResponse] = await Promise.all([
          apiClient.listMembers(),
          apiClient.listSeasons({ limit: 50 })
        ]);
        const seasons = seasonsResponse.seasons;
        const initialSeason = seasons.find((s) => s.status === "active") ?? seasons[0] ?? null;
        if (initialSeason === null) {
          if (!cancelled) {
            setState({
              status: "ready",
              seasons,
              selectedSeasonID: "",
              members: membersResponse.members,
              ledger: [],
              ledgerLoading: false,
              errorMessage: null
            });
          }
          return;
        }
        const ledger = await fetchAllLedger(initialSeason.id);
        if (cancelled) {
          return;
        }
        setState({
          status: "ready",
          seasons,
          selectedSeasonID: initialSeason.id,
          members: membersResponse.members,
          ledger,
          ledgerLoading: false,
          errorMessage: null
        });
      } catch (caught) {
        if (cancelled) {
          return;
        }
        setState({
          status: "error",
          seasons: [],
          selectedSeasonID: "",
          members: [],
          ledger: [],
          ledgerLoading: false,
          errorMessage: messageFor(caught)
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(
    () => buildRows(state.members, state.ledger),
    [state.members, state.ledger]
  );

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (trimmed === "") {
      return rows;
    }
    return rows.filter((row) => {
      const haystack = `${row.email} ${row.name} ${row.role}`.toLowerCase();
      return haystack.includes(trimmed);
    });
  }, [rows, query]);

  const sorted = useMemo(() => sortRows(filtered, sort), [filtered, sort]);

  function toggleSort(key: SortKey) {
    setSort((previous) => {
      if (previous.key === key) {
        return { key, dir: previous.dir === "asc" ? "desc" : "asc" };
      }
      // Numeric columns default to desc; text columns to asc.
      const numericKeys: SortKey[] = ["available", "received", "given", "coinsGiven"];
      return { key, dir: numericKeys.includes(key) ? "desc" : "asc" };
    });
  }

  if (state.status === "loading") {
    return <p className="loading">Loading the people directory…</p>;
  }
  if (state.status === "error") {
    return (
      <div className="card">
        <h2>Couldn't load people</h2>
        <p>{state.errorMessage}</p>
      </div>
    );
  }

  const selectedSeason = state.seasons.find((s) => s.id === state.selectedSeasonID) ?? null;

  return (
    <div className="people-view">
      <header className="card people-view__header">
        <div>
          <h2>People</h2>
          <p className="muted">
            {state.members.length} active member{state.members.length === 1 ? "" : "s"}
            {selectedSeason !== null
              ? <> · season <strong>{selectedSeason.name}</strong> · {selectedSeason.status}</>
              : <> · no seasons</>}
          </p>
        </div>
        <input
          className="people-view__search"
          type="search"
          placeholder="Search by email, name, or role…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </header>

      <div className="card">
        <div className="filter-row">
          <label>
            Season
            <select
              value={state.selectedSeasonID}
              onChange={(event) => void changeSeason(event.target.value)}
              disabled={state.ledgerLoading || state.seasons.length === 0}
            >
              {state.seasons.length === 0 && <option value="">No seasons</option>}
              {state.seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name} · {season.status}
                </option>
              ))}
            </select>
          </label>
          {state.ledgerLoading && <span className="muted">Loading season totals…</span>}
        </div>
      </div>

      {roleError !== null && (
        <div className="card error-banner" role="alert">
          <p>{roleError}</p>
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="card empty-state">
          <p className="muted">No members match the current filter.</p>
        </div>
      ) : (
        <section className="card">
          <table className="people-table">
            <thead>
              <tr>
                <SortableHeader label="Email" sortKey="email" sort={sort} onClick={toggleSort} align="left" />
                <SortableHeader label="Role" sortKey="role" sort={sort} onClick={toggleSort} align="left" />
                <SortableHeader label="Coins available" sortKey="available" sort={sort} onClick={toggleSort} align="right" />
                <SortableHeader label="Coins given" sortKey="coinsGiven" sort={sort} onClick={toggleSort} align="right" />
                <SortableHeader label="Gumballs received" sortKey="received" sort={sort} onClick={toggleSort} align="right" />
                <SortableHeader label="Gumballs given" sortKey="given" sort={sort} onClick={toggleSort} align="right" />
                {canDeleteMembers && <th className="people-table__th people-table__th--right" aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="people-table__person">
                      <strong>{row.email}</strong>
                      {row.name !== "" && <span className="muted">{row.name}</span>}
                    </div>
                  </td>
                  <td>
                    {canChangeRoles && row.id !== currentUserID ? (
                      <RoleSelect
                        role={row.role}
                        disabled={savingId === row.id}
                        onChange={(next) => void handleRoleChange(row.id, next)}
                      />
                    ) : (
                      <RolePill role={row.role} />
                    )}
                  </td>
                  <td className="people-table__num">{formatNumber(row.availableCoins)}</td>
                  <td className="people-table__num">{formatNumber(row.coinsGiven)}</td>
                  <td className="people-table__num">{formatNumber(row.gumballsReceived)}</td>
                  <td className="people-table__num">{formatNumber(row.gumballsGiven)}</td>
                  {canDeleteMembers && (
                    <td className="people-table__num">
                      {row.id !== currentUserID && (
                        <button
                          type="button"
                          className="btn btn--danger btn--sm"
                          disabled={deletingId === row.id}
                          onClick={() => void handleDelete(row.id, row.email)}
                        >
                          {deletingId === row.id ? "Removing…" : "Remove"}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function RolePill({ role }: { role: string }) {
  return <span className={`role-pill role-pill--${role}`}>{role}</span>;
}

function RoleSelect({
  role,
  disabled,
  onChange
}: {
  role: string;
  disabled: boolean;
  onChange: (role: string) => void;
}) {
  return (
    <select
      className={`role-select role-select--${role}`}
      value={role}
      disabled={disabled}
      onChange={(event) => {
        const next = event.target.value;
        if (next !== role) {
          onChange(next);
        }
      }}
    >
      {ASSIGNABLE_ROLES.map((value) => (
        <option key={value} value={value}>{value}</option>
      ))}
    </select>
  );
}

function buildRows(members: Member[], entries: LedgerEntry[]): PersonRow[] {
  // available   = coinsGiven - (spends + season expiry)
  // received    = sum of incoming gumball_spend
  // given       = sum of outgoing gumball_spend
  // coinsGiven  = sum of coin_grant + signed manual_adjustment to this user
  // For non-IC roles the ledger should be empty for them, so all four
  // numbers fall to zero — the table still shows the row.
  const available = new Map<string, number>();
  const received = new Map<string, number>();
  const given = new Map<string, number>();
  const coinsGiven = new Map<string, number>();

  function bump(map: Map<string, number>, key: string | null, delta: number) {
    if (key === null) {
      return;
    }
    map.set(key, (map.get(key) ?? 0) + delta);
  }

  for (const entry of entries) {
    if (entry.entry_type === "coin_grant") {
      bump(available, entry.target_user_id, entry.amount);
      bump(coinsGiven, entry.target_user_id, entry.amount);
    } else if (entry.entry_type === "manual_adjustment") {
      // amount is signed for adjustments (positive = credit, negative = debit).
      bump(available, entry.target_user_id, entry.amount);
      bump(coinsGiven, entry.target_user_id, entry.amount);
    } else if (entry.entry_type === "gumball_spend") {
      bump(available, entry.source_user_id, -entry.amount);
      bump(given, entry.source_user_id, entry.amount);
      bump(received, entry.target_user_id, entry.amount);
    } else if (entry.entry_type === "season_expiry") {
      bump(available, entry.source_user_id, -entry.amount);
    } else if (entry.entry_type === "season_carryover") {
      bump(available, entry.target_user_id, entry.amount);
      bump(coinsGiven, entry.target_user_id, entry.amount);
    }
  }

  return members.map((member) => ({
    id: member.id,
    email: member.email,
    name: member.name,
    role: member.role,
    availableCoins: available.get(member.id) ?? 0,
    gumballsReceived: received.get(member.id) ?? 0,
    gumballsGiven: given.get(member.id) ?? 0,
    coinsGiven: coinsGiven.get(member.id) ?? 0
  }));
}

function sortRows(rows: PersonRow[], sort: SortState): PersonRow[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  const copy = [...rows];
  copy.sort((a, b) => {
    switch (sort.key) {
      case "email":
        return a.email.localeCompare(b.email) * dir;
      case "role": {
        const aRank = roleRank[a.role] ?? 99;
        const bRank = roleRank[b.role] ?? 99;
        if (aRank !== bRank) {
          return (aRank - bRank) * dir;
        }
        return a.email.localeCompare(b.email);
      }
      case "available":
        return (a.availableCoins - b.availableCoins) * dir;
      case "received":
        return (a.gumballsReceived - b.gumballsReceived) * dir;
      case "given":
        return (a.gumballsGiven - b.gumballsGiven) * dir;
      case "coinsGiven":
        return (a.coinsGiven - b.coinsGiven) * dir;
      default:
        return 0;
    }
  });
  return copy;
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Could not load people directory.";
}
